import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Loader2 } from "lucide-react";

import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";

import {
  UF_OPTIONS,
  cadastroFormSchema,
  formatCEP,
  formatCNPJ,
  formatPhone,
  step1Schema,
  step2Schema,
  step3Schema,
  type CadastroFormData,
} from "./schema";

type FormState = Partial<Record<keyof CadastroFormData, string | boolean>>;
type FieldErrors = Partial<Record<keyof CadastroFormData, string>>;

const TOTAL_STEPS = 3;

const STEP_SCHEMAS = [step1Schema, step2Schema, step3Schema] as const;

const inputClass =
  "h-14 w-full rounded-xl border border-zinc-200 bg-white px-4 text-base text-[#112711] placeholder:text-zinc-400 outline-none transition-colors focus:border-[#4BD084] focus:ring-2 focus:ring-[#4BD084]/30";

const labelClass =
  "block text-sm font-medium text-zinc-600 mb-1.5";

const variants = {
  enter: (dir: number) => ({ x: dir >= 0 ? 48 : -48, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir: number) => ({ x: dir >= 0 ? -48 : 48, opacity: 0 }),
};

export const ParceiroCadastroForm = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(0);
  const [data, setData] = useState<FormState>({});
  const [errors, setErrors] = useState<FieldErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  const set = (key: keyof CadastroFormData, value: string | boolean) => {
    setData((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  // Auto-focus no primeiro campo de cada step (após a transição)
  useEffect(() => {
    const t = setTimeout(() => {
      const el = containerRef.current?.querySelector<HTMLElement>(
        "[data-autofocus]"
      );
      el?.focus();
    }, 60);
    return () => clearTimeout(t);
  }, [step]);

  const applyErrors = (issues: { path: (string | number)[]; message: string }[]) => {
    const fieldErrors: FieldErrors = {};
    issues.forEach((issue) => {
      const key = issue.path[0] as keyof CadastroFormData;
      if (key && !fieldErrors[key]) fieldErrors[key] = issue.message;
    });
    setErrors(fieldErrors);
  };

  const validateStep = (s: number): boolean => {
    const schema = STEP_SCHEMAS[s - 1];
    const parsed = schema.safeParse(data);
    if (!parsed.success) {
      applyErrors(parsed.error.issues);
      return false;
    }
    return true;
  };

  // Busca endereço pelo CEP (ViaCEP). Falha silenciosa.
  const handleCepBlur = async () => {
    const cep = String(data.pj_cep ?? "").replace(/\D/g, "");
    if (cep.length !== 8) return;
    setCepLoading(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const json = (await res.json()) as {
        logradouro?: string;
        bairro?: string;
        localidade?: string;
        uf?: string;
        erro?: boolean;
      };
      if (json.erro) return;
      setData((prev) => ({
        ...prev,
        pj_logradouro: json.logradouro || (prev.pj_logradouro as string) || "",
        pj_bairro: json.bairro || (prev.pj_bairro as string) || "",
        pj_cidade: json.localidade || (prev.pj_cidade as string) || "",
        pj_uf: json.uf || (prev.pj_uf as string) || "",
      }));
    } catch {
      /* silencioso */
    } finally {
      setCepLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (step < TOTAL_STEPS) {
      if (!validateStep(step)) return;
      setDirection(1);
      setStep((s) => s + 1);
      return;
    }

    // Step final
    const parsed = cadastroFormSchema.safeParse(data);
    if (!parsed.success) {
      applyErrors(parsed.error.issues);
      toast({
        title: "Confira os dados",
        description:
          parsed.error.issues[0]?.message ?? "Preencha todos os campos.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await fetch("/api/parceiro-cadastro", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          autoriza_contato: parsed.data.autoriza_contato,
          fullname: parsed.data.fullname,
          email: parsed.data.email,
          phone: parsed.data.phone,
          pj_razao_social: parsed.data.pj_razao_social,
          pj_cnpj: parsed.data.pj_cnpj,
          pj_cep: parsed.data.pj_cep,
          pj_logradouro: parsed.data.pj_logradouro,
          pj_numero: parsed.data.pj_numero,
          pj_complemento: parsed.data.pj_complemento || undefined,
          pj_bairro: parsed.data.pj_bairro,
          pj_cidade: parsed.data.pj_cidade,
          pj_uf: parsed.data.pj_uf,
          pj_email: parsed.data.pj_email,
        }),
      });
      if (!res.ok) throw new Error(`Submit failed (${res.status})`);
      navigate("/parceiro/cadastro/obrigado");
    } catch (err) {
      console.error("[parceiro-cadastro] submit error:", err);
      toast({
        title: "Erro ao enviar",
        description:
          "Tente novamente em instantes. Se persistir, fale com a gente pelo WhatsApp.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    setDirection(-1);
    setStep((s) => Math.max(1, s - 1));
  };

  const err = (key: keyof CadastroFormData) =>
    errors[key] ? (
      <p className="mt-1.5 text-xs text-red-500">{errors[key]}</p>
    ) : null;

  // helper de input (função, não componente — evita remount/perda de foco)
  const textInput = (
    key: keyof CadastroFormData,
    label: string,
    opts: {
      placeholder?: string;
      type?: string;
      inputMode?: "text" | "email" | "tel" | "numeric";
      autoComplete?: string;
      format?: (v: string) => string;
      onBlur?: () => void;
      autoFocus?: boolean;
    } = {}
  ) => (
    <div>
      <label htmlFor={key} className={labelClass}>
        {label}
      </label>
      <input
        id={key}
        type={opts.type ?? "text"}
        inputMode={opts.inputMode}
        autoComplete={opts.autoComplete}
        value={String(data[key] ?? "")}
        onChange={(e) =>
          set(key, opts.format ? opts.format(e.target.value) : e.target.value)
        }
        onBlur={opts.onBlur}
        placeholder={opts.placeholder}
        className={inputClass}
        aria-invalid={!!errors[key]}
        {...(opts.autoFocus ? { "data-autofocus": true } : {})}
      />
      {err(key)}
    </div>
  );

  const stepTitles = [
    "Vamos formalizar sua parceria",
    "Seus dados pessoais",
    "Dados da sua empresa",
  ];
  const stepSubtitles = [
    "Leva menos de 2 minutos.",
    "Para entrarmos em contato e formalizar.",
    "Dados da empresa que serão usados no contrato.",
  ];

  return (
    <div>
      {/* Progresso */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold tracking-[0.14em] uppercase text-zinc-400">
            Passo {step} de {TOTAL_STEPS}
          </span>
          <span className="text-xs text-zinc-400">
            {Math.round((step / TOTAL_STEPS) * 100)}%
          </span>
        </div>
        <div className="h-1.5 w-full rounded-full bg-zinc-100 overflow-hidden">
          <motion.div
            className="h-full rounded-full bg-[#4BD084]"
            initial={false}
            animate={{ width: `${(step / TOTAL_STEPS) * 100}%` }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          />
        </div>
      </div>

      <form onSubmit={handleSubmit} noValidate ref={containerRef}>
        <div className="relative overflow-hidden">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={step}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.28, ease: "easeOut" }}
            >
              {/* Título do passo */}
              <h2
                className="text-2xl sm:text-3xl font-medium text-[#112711] leading-tight"
                style={{
                  fontFamily:
                    '"Playfair Display", Georgia, "Times New Roman", serif',
                  letterSpacing: "-0.01em",
                }}
              >
                {stepTitles[step - 1]}
              </h2>
              <p className="mt-2 text-sm text-zinc-500">
                {stepSubtitles[step - 1]}
              </p>

              <div className="mt-7 space-y-5">
                {/* STEP 1 — consentimento */}
                {step === 1 && (
                  <div>
                    <label
                      className={`flex items-start gap-3 rounded-2xl border p-4 sm:p-5 cursor-pointer transition-colors ${
                        data.autoriza_contato
                          ? "border-[#4BD084] bg-[#4BD084]/5"
                          : "border-zinc-200 hover:border-zinc-300"
                      }`}
                    >
                      <Checkbox
                        data-autofocus
                        checked={!!data.autoriza_contato}
                        onCheckedChange={(c) =>
                          set("autoriza_contato", c === true)
                        }
                        className="mt-0.5 h-5 w-5 border-zinc-300 data-[state=checked]:bg-[#4BD084] data-[state=checked]:border-[#4BD084] data-[state=checked]:text-[#112711]"
                      />
                      <span className="text-sm sm:text-[15px] text-[#112711] leading-relaxed">
                        Confirmo meu interesse em participar do{" "}
                        <span className="font-semibold">
                          programa de parceiros Allu Invest
                        </span>{" "}
                        e autorizo a equipe da Allu a entrar em contato para
                        formalizar a parceria.
                      </span>
                    </label>
                    {err("autoriza_contato")}
                  </div>
                )}

                {/* STEP 2 — dados pessoais */}
                {step === 2 && (
                  <>
                    {textInput("fullname", "Nome completo", {
                      placeholder: "Seu nome e sobrenome",
                      autoComplete: "name",
                      autoFocus: true,
                    })}
                    {textInput("email", "E-mail", {
                      placeholder: "voce@email.com.br",
                      type: "email",
                      inputMode: "email",
                      autoComplete: "email",
                    })}
                    {textInput("phone", "Telefone / WhatsApp", {
                      placeholder: "(11) 99999-9999",
                      type: "tel",
                      inputMode: "tel",
                      autoComplete: "tel",
                      format: formatPhone,
                    })}
                  </>
                )}

                {/* STEP 3 — dados da PJ */}
                {step === 3 && (
                  <>
                    {textInput("pj_razao_social", "Razão social", {
                      placeholder: "Nome empresarial completo",
                      autoFocus: true,
                    })}
                    {textInput("pj_cnpj", "CNPJ", {
                      placeholder: "00.000.000/0000-00",
                      inputMode: "numeric",
                      format: formatCNPJ,
                    })}
                    <div>
                      {textInput("pj_cep", "CEP", {
                        placeholder: "00000-000",
                        inputMode: "numeric",
                        format: formatCEP,
                        onBlur: handleCepBlur,
                      })}
                      {cepLoading && (
                        <p className="mt-1.5 text-xs text-zinc-400 flex items-center gap-1.5">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          Buscando endereço…
                        </p>
                      )}
                    </div>
                    {textInput("pj_logradouro", "Logradouro (rua/avenida)", {
                      placeholder: "Rua, avenida…",
                      autoComplete: "address-line1",
                    })}
                    <div className="grid grid-cols-2 gap-4">
                      {textInput("pj_numero", "Número", {
                        placeholder: "123",
                        inputMode: "numeric",
                      })}
                      {textInput("pj_complemento", "Complemento", {
                        placeholder: "Sala, andar (opcional)",
                      })}
                    </div>
                    {textInput("pj_bairro", "Bairro", {
                      placeholder: "Bairro",
                    })}
                    <div className="grid grid-cols-[1fr_auto] gap-4">
                      {textInput("pj_cidade", "Cidade", {
                        placeholder: "Cidade",
                      })}
                      <div className="min-w-[140px]">
                        <label htmlFor="pj_uf" className={labelClass}>
                          UF
                        </label>
                        <Select
                          value={String(data.pj_uf ?? "")}
                          onValueChange={(v) => set("pj_uf", v)}
                        >
                          <SelectTrigger
                            id="pj_uf"
                            className="h-14 rounded-xl border-zinc-200 bg-white text-base text-[#112711] focus:ring-2 focus:ring-[#4BD084]/30"
                            aria-invalid={!!errors.pj_uf}
                          >
                            <SelectValue placeholder="UF" />
                          </SelectTrigger>
                          <SelectContent>
                            {UF_OPTIONS.map((uf) => (
                              <SelectItem key={uf.value} value={uf.value}>
                                {uf.value} — {uf.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {err("pj_uf")}
                      </div>
                    </div>
                    {textInput("pj_email", "E-mail da empresa", {
                      placeholder: "contato@empresa.com.br",
                      type: "email",
                      inputMode: "email",
                    })}
                  </>
                )}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Navegação */}
        <div className="mt-8 flex items-center gap-3">
          {step > 1 && (
            <button
              type="button"
              onClick={handleBack}
              className="inline-flex items-center justify-center gap-2 h-12 px-5 rounded-full font-semibold text-sm text-[#112711] border border-zinc-200 hover:bg-zinc-50 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar
            </button>
          )}
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 inline-flex items-center justify-center gap-2 h-12 sm:h-14 rounded-full font-semibold text-sm bg-[#4BD084] hover:bg-[#5fdc94] text-[#112711] shadow-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Enviando…
              </>
            ) : step < TOTAL_STEPS ? (
              <>
                Continuar
                <ArrowRight className="w-4 h-4" />
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Enviar cadastro
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};
